import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Image as ImageIcon } from 'lucide-react';
import api from '../api';

export default function CreateBannerPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        title: '',
        desktop_image_url: '',
        mobile_image_url: '',
        link: '',
        is_active: true,
        order: 0
    });
    const [loading, setLoading] = useState(false);
    const [loadingForm, setLoadingForm] = useState(!!id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (id) {
            loadBanner();
        }
    }, [id]);

    const loadBanner = async () => {
        try {
            const data = await api.getAllBannersAdmin();
            const banner = data.find(b => b.id == id);
            if (banner) {
                setFormData({
                    title: banner.title || '',
                    desktop_image_url: banner.desktop_image_url || '',
                    mobile_image_url: banner.mobile_image_url || '',
                    link: banner.link || '',
                    is_active: banner.is_active ?? true,
                    order: banner.order ?? 0
                });
            }
        } catch (error) {
            console.error('Error loading banner:', error);
            alert('Error loading banner');
        } finally {
            setLoadingForm(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (id) {
                await api.updateBanner(id, formData);
            } else {
                await api.createBanner(formData);
            }
            navigate('/admin', { state: { activeTab: 'banners' } });
        } catch (error) {
            alert('Error saving banner: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (loadingForm) {
        return (
            <main className="main">
                <div className="skeleton" style={{ height: '100vh' }} />
            </main>
        );
    }

    return (
        <main className="main" style={{ background: 'var(--bg-admin)', minHeight: '100vh' }}>
            <div className="container" style={{ maxWidth: '800px', padding: '40px 24px' }}>
                {/* Header */}
                <div style={{ marginBottom: '32px' }}>
                    <button
                        onClick={() => navigate('/admin', { state: { activeTab: 'banners' } })}
                        className="btn btn-ghost"
                        style={{ marginBottom: '16px', padding: '8px 16px' }}
                    >
                        <ArrowLeft size={18} /> Back to Banners
                    </button>
                    <h1 style={{ fontSize: '32px', fontWeight: '700', margin: 0 }}>
                        {id ? 'Edit Banner' : 'Create Banner'}
                    </h1>
                </div>

                {/* Form Card */}
                <div className="card" style={{ padding: '32px' }}>
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label className="form-label">Title *</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                required
                                placeholder="e.g. Pongal Festival 2026"
                            />
                        </div>

                        {/* Generate from Text Option */}
                        <div className="form-group" style={{
                            background: 'var(--bg-secondary)',
                            padding: '16px',
                            borderRadius: '8px',
                            marginBottom: '16px',
                            display: 'none' // Hidden for now
                        }}>
                            <label className="form-label" style={{ marginBottom: '12px' }}>
                                ✨ Generate Banner from Text (Optional)
                            </label>
                            <div style={{ display: 'grid', gap: '12px' }}>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Banner subtitle (optional)"
                                    id="bannerSubtitle"
                                />
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                    <input
                                        type="color"
                                        defaultValue="#1a1a2e"
                                        id="bannerBgColor"
                                        style={{ height: '40px', cursor: 'pointer' }}
                                        title="Background Color"
                                    />
                                    <input
                                        type="color"
                                        defaultValue="#ffffff"
                                        id="bannerTextColor"
                                        style={{ height: '40px', cursor: 'pointer' }}
                                        title="Text Color"
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={async () => {
                                        const subtitle = document.getElementById('bannerSubtitle').value;
                                        const bgColor = document.getElementById('bannerBgColor').value;
                                        const textColor = document.getElementById('bannerTextColor').value;

                                        if (!formData.title) {
                                            alert('Please enter a title first');
                                            return;
                                        }

                                        try {
                                            setLoading(true);
                                            const result = await api.generateBanner(
                                                formData.title,
                                                subtitle,
                                                bgColor,
                                                textColor
                                            );
                                            const fullUrl = `http://localhost:8000${result.url}`;
                                            setFormData({ ...formData, desktop_image_url: fullUrl });
                                        } catch (error) {
                                            alert('Generation failed: ' + error.message);
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                    disabled={loading || !formData.title}
                                >
                                    🎨 Generate Desktop Banner
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={async () => {
                                        const subtitle = document.getElementById('bannerSubtitle').value;
                                        const bgColor = document.getElementById('bannerBgColor').value;
                                        const textColor = document.getElementById('bannerTextColor').value;

                                        if (!formData.title) {
                                            alert('Please enter a title first');
                                            return;
                                        }

                                        try {
                                            setLoading(true);
                                            const result = await api.generateBanner(
                                                formData.title,
                                                subtitle,
                                                bgColor,
                                                textColor,
                                                800,  // Square width for mobile
                                                800   // Square height for mobile
                                            );
                                            const fullUrl = `http://localhost:8000${result.url}`;
                                            setFormData({ ...formData, mobile_image_url: fullUrl });
                                        } catch (error) {
                                            alert('Generation failed: ' + error.message);
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                    disabled={loading || !formData.title}
                                >
                                    📱 Generate Mobile Banner (Square)
                                </button>
                            </div>
                            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
                                💡 Enter title above, optionally add subtitle and choose colors, then click generate
                            </p>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                <ImageIcon size={16} /> Desktop Image * (Recommended: 1920x600px)
                            </label>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={async (e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            try {
                                                setLoading(true);
                                                const result = await api.uploadImage(file);
                                                const fullUrl = `http://localhost:8000${result.url}`;
                                                setFormData({ ...formData, desktop_image_url: fullUrl });
                                            } catch (error) {
                                                alert('Upload failed: ' + error.message);
                                            } finally {
                                                setLoading(false);
                                            }
                                        }
                                    }}
                                    className="form-input"
                                    style={{ flex: 1 }}
                                />
                            </div>
                            <input
                                type="url"
                                className="form-input"
                                value={formData.desktop_image_url}
                                onChange={(e) => setFormData({ ...formData, desktop_image_url: e.target.value })}
                                required
                                placeholder="Or paste URL: https://..."
                            />
                            {formData.desktop_image_url && (
                                <img
                                    src={formData.desktop_image_url}
                                    alt="Desktop preview"
                                    style={{ width: '100%', marginTop: '8px', borderRadius: '8px' }}
                                    onError={(e) => e.target.style.display = 'none'}
                                />
                            )}
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                <ImageIcon size={16} /> Mobile Image (Optional - Recommended: 800x800px square)
                            </label>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={async (e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            try {
                                                setLoading(true);
                                                const result = await api.uploadImage(file);
                                                const fullUrl = `http://localhost:8000${result.url}`;
                                                setFormData({ ...formData, mobile_image_url: fullUrl });
                                            } catch (error) {
                                                alert('Upload failed: ' + error.message);
                                            } finally {
                                                setLoading(false);
                                            }
                                        }
                                    }}
                                    className="form-input"
                                    style={{ flex: 1 }}
                                />
                            </div>
                            <input
                                type="url"
                                className="form-input"
                                value={formData.mobile_image_url}
                                onChange={(e) => setFormData({ ...formData, mobile_image_url: e.target.value })}
                                placeholder="Or paste URL: https://... (Leave empty to use desktop image)"
                            />
                            {formData.mobile_image_url && (
                                <img
                                    src={formData.mobile_image_url}
                                    alt="Mobile preview"
                                    style={{ width: '200px', marginTop: '8px', borderRadius: '8px' }}
                                    onError={(e) => e.target.style.display = 'none'}
                                />
                            )}
                            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                                💡 Tip: Use square or vertical images for better mobile display
                            </p>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Link URL (Optional)</label>
                            <input
                                type="url"
                                className="form-input"
                                value={formData.link}
                                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                                placeholder="https://..."
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div className="form-group">
                                <label className="form-label">Order</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.order}
                                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                                    min="0"
                                />
                                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                                    Lower numbers appear first
                                </p>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Status</label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    />
                                    <span>{formData.is_active ? 'Active' : 'Inactive'}</span>
                                </label>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => navigate('/admin', { state: { activeTab: 'banners' } })}
                                style={{ flex: 1 }}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                style={{ flex: 1 }}
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : (id ? 'Update Banner' : 'Create Banner')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </main>
    );
}

